import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, MapPin, Plus, Trash2 } from 'lucide-react';
import { useTheme, useThemeMode, RADIUS, TYPE } from '../theme';
import { Badge, Button, Card, EmptyState, Icon, IconButton, Loader } from '../components/ui';
import {
  huntApi, kr, reportStatusText, chargeStatusText, possibleDuplicates, notifyHuntEconomyChanged, alderValue, alderLabel,
  deleteReportConfirmText, deleteChargeConfirmText, efterJaktCollapsedKey, readCollapsed, writeCollapsed,
  GAME_OPTIONS, KON_OPTIONS, ALDER_OPTIONS, HUNT_ECONOMY_CHANGED, EFTERJAKT_FOCUS_KEY,
  type AfterHunt, type ChargeSummary, type HuntCharge, type KillItemInput, type KillReport,
} from '../huntEconomy';

// ─────────────────────────────────────────────────────────────────
// P4 · Efter jakt: varje jägare anger sitt fällda vilt en gång per jakt (kan göras senare),
// jaktledaren bekräftar (D3) → summering med fällavgifter och böter → betala med kort eller
// Swish/kontant till jaktledaren. API: services/huntKills.ts, huntCharges.ts.
// ─────────────────────────────────────────────────────────────────

const dateText = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const timeText = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : '');

type Row = KillItemInput & { key: string };
let rowSeq = 0;
const newRow = (r: Partial<KillItemInput> = {}): Row => ({ key: `r${++rowSeq}`, viltslag: '', kon: 'Okänt', alder: 'Vuxen', antal: 1, ...r });
const toInput = (rows: Row[]): KillItemInput[] => rows.filter(r => r.viltslag.trim()).map(({ key: _k, ...r }) => ({ ...r, alder: alderValue(r.alder), antal: Math.max(1, Number(r.antal) || 1) }));

/** Raderna i en summering. */
export function SummaryLines({ summary, compact }: { summary: ChargeSummary; compact?: boolean }) {
  const T = useTheme();
  if (!summary.lines.length) return <div style={{ ...TYPE.small, color: T.textMuted }}>Inga avgifter.</div>;
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      {summary.lines.map((l, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, justifyContent: 'space-between', fontSize: compact ? 13 : 14, color: l.kind === 'info' ? T.textMuted : T.text }}>
          <span style={{ minWidth: 0 }}>
            {l.kind === 'info' ? l.note : <>
              {l.antal > 1 ? `${l.antal} × ` : ''}{l.viltslag}{l.kon && l.kon !== 'Okänt' ? ` ${l.kon.toLowerCase()}` : ''}{l.alder && l.alder !== 'Vuxen' && l.alder !== 'Okänt' ? ` (${alderLabel(l.alder).toLowerCase()})` : ''}
              {l.kind === 'bot' && <span style={{ color: T.danger, fontWeight: 700 }}> · bot</span>}
              {l.note && l.kind !== 'bot' && <span style={{ color: T.textMuted }}> · {l.note}</span>}
            </>}
          </span>
          {l.kind !== 'info' && <span style={{ fontWeight: 700, whiteSpace: 'nowrap', color: l.kind === 'bot' ? T.danger : T.text }}>{kr(l.amountKr)}</span>}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${T.border}`, paddingTop: 6, marginTop: 2, fontWeight: 800 }}>
        <span>{summary.preliminary ? 'Preliminärt att betala' : 'Att betala'}</span><span>{kr(summary.totalKr)}</span>
      </div>
    </div>
  );
}

/** Formuläret för fällt vilt – för jägaren själv eller jaktledaren som rättar/fyller i. */
export function KillReportForm({ markId, report, mode, onDone, onCancel }: {
  markId: string; report: KillReport; mode: 'hunter' | 'leader'; onDone: (r: KillReport) => void; onCancel?: () => void;
}) {
  const T = useTheme();
  const [rows, setRows] = useState<Row[]>(() => report.items.length
    ? report.items.map(i => newRow({ ...i, alder: alderLabel(i.alder) || 'Okänt', kon: i.kon || 'Okänt' }))
    : []);
  const [nothing, setNothing] = useState(report.status !== 'pending' && report.nothingShot);
  const [preview, setPreview] = useState<ChargeSummary | null>(null);
  const { mode: themeMode } = useThemeMode();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const usedPins = new Set(rows.map(r => r.pinId).filter(Boolean));
  const suggestions = report.suggestions.filter(s => !usedPins.has(s.pinId));
  const timer = useRef<number | undefined>(undefined);

  // Förhandsvisning med jägarens priser medan man fyller i (jaktledaren räknar åt jägaren).
  useEffect(() => {
    window.clearTimeout(timer.current);
    const items = nothing ? [] : toInput(rows);
    timer.current = window.setTimeout(() => {
      huntApi.killReportPreview(markId, items, mode === 'leader' ? report.hunterEmail : null).then(setPreview).catch(() => setPreview(null));
    }, 350);
    return () => window.clearTimeout(timer.current);
  }, [rows, nothing, markId, mode, report.hunterEmail]);

  const update = (key: string, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));
  const forbidden = new Set((preview?.lines ?? []).filter(l => l.kind === 'bot').map(l => l.viltslag));

  const save = async () => {
    setErr('');
    const items = toInput(rows);
    if (!nothing && !items.length) { setErr('Lägg till minst ett djur eller välj "Inget fällt".'); return; }
    if (mode === 'leader' && !confirm(`Bekräfta ${report.hunterName || report.hunterEmail}s rapport? ${nothing ? 'Inget fällt.' : 'Avgiften räknas och jägaren får en notis.'}`)) return;
    setBusy(true);
    try {
      const r = mode === 'hunter'
        ? await huntApi.submitKillReport(report.meetingId, nothing, nothing ? [] : items)
        : await huntApi.confirmKillReport(report.id, { nothingShot: nothing, items: nothing ? [] : items });
      notifyHuntEconomyChanged();
      onDone(r);
    } catch (e: any) { setErr(e.message || 'Något gick fel'); }
    setBusy(false);
  };

  const field: React.CSSProperties = { minHeight: 44, padding: '0 10px', borderRadius: RADIUS.sm, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontFamily: 'inherit', fontSize: 15, minWidth: 0 };
  return (
    <div data-testid="kill-report-form" style={{ display: 'grid', gap: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, fontWeight: 700, cursor: 'pointer' }}>
        <input type="checkbox" checked={nothing} onChange={e => setNothing(e.target.checked)} style={{ width: 20, height: 20, accentColor: T.accentFill, colorScheme: themeMode }} />
        Inget fällt
      </label>
      {!nothing && <>
        {suggestions.length > 0 && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ ...TYPE.small, color: T.textSub }}>Från kartan under jakten</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {suggestions.map(s => (
                <Button key={s.pinId} size="sm" variant="secondary" onClick={() => setRows(rs => [...rs, newRow({ viltslag: s.viltslag, kon: s.kon || 'Okänt', alder: alderLabel(s.alder) || 'Okänt', pinId: s.pinId, lat: s.lat, lng: s.lng })])}>
                  <Icon as={MapPin} size={16} /> {s.viltslag}{s.kon ? ` ${s.kon.toLowerCase()}` : ''} – lägg till
                </Button>
              ))}
            </div>
          </div>
        )}
        <datalist id={`viltslag-${report.id}`}>{GAME_OPTIONS.map(g => <option key={g} value={g} />)}</datalist>
        {rows.map((r, i) => (
          <div key={r.key} data-testid="kill-row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8, padding: 10, borderRadius: RADIUS.md, border: `1px solid ${forbidden.has(r.viltslag.trim()) ? T.danger : T.border}`, background: T.surface2 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 8, minWidth: 0 }}>
              <input aria-label={`Viltslag ${i + 1}`} list={`viltslag-${report.id}`} placeholder="Viltslag" value={r.viltslag} onChange={e => update(r.key, { viltslag: e.target.value })} style={{ ...field, gridColumn: '1 / -1' }} />
              <select aria-label={`Kön ${i + 1}`} value={r.kon || 'Okänt'} onChange={e => update(r.key, { kon: e.target.value })} style={field}>{KON_OPTIONS.map(k => <option key={k}>{k}</option>)}</select>
              <select aria-label={`Ålder ${i + 1}`} value={r.alder || 'Okänt'} onChange={e => update(r.key, { alder: e.target.value })} style={field}>{ALDER_OPTIONS.map(k => <option key={k}>{k}</option>)}</select>
              <input aria-label={`Antal ${i + 1}`} type="number" min={1} max={50} inputMode="numeric" value={r.antal ?? 1} onChange={e => update(r.key, { antal: Number(e.target.value) })} style={field} />
            </div>
            <IconButton label={`Ta bort rad ${i + 1}`} onClick={() => setRows(rs => rs.filter(x => x.key !== r.key))}><Icon as={Trash2} /></IconButton>
            {r.pinId && <div style={{ gridColumn: '1 / -1', fontSize: 12, color: T.textMuted }}>Position från kartan</div>}
            {forbidden.has(r.viltslag.trim()) && <div role="alert" style={{ gridColumn: '1 / -1', fontSize: 13, fontWeight: 700, color: T.danger }}>Viltslaget är inte tillåtet på marken – bot debiteras.</div>}
          </div>
        ))}
        <div><Button variant="secondary" onClick={() => setRows(rs => [...rs, newRow()])}><Icon as={Plus} size={18} /> Lägg till djur</Button></div>
      </>}
      {preview && !nothing && (
        <div style={{ padding: 12, borderRadius: RADIUS.md, background: T.surface2 }}>
          <div style={{ ...TYPE.label, color: T.textSub, marginBottom: 6 }}>{mode === 'hunter' ? 'Preliminär summering' : `Summering för ${report.hunterName || report.hunterEmail}`}</div>
          <SummaryLines summary={preview} compact />
          {preview.notes.map(n => <div key={n} style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{n}</div>)}
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, fontWeight: 600, fontSize: 14 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button onClick={save} disabled={busy}>{mode === 'hunter' ? (report.status === 'pending' ? 'Skicka till jaktledaren' : 'Spara ändringar') : 'Spara och bekräfta'}</Button>
        {onCancel && <Button variant="ghost" onClick={onCancel} disabled={busy}>Avbryt</Button>}
      </div>
    </div>
  );
}

/** Jägarens avgift: status och betalning. */
export function ChargeBox({ charge }: { charge: HuntCharge }) {
  const T = useTheme();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const st = chargeStatusText(charge);
  const pay = async () => {
    setErr(''); setBusy(true);
    try { window.location.assign(await huntApi.startHuntChargeCheckout(charge.id)); } catch (e: any) { setErr(e.message || 'Kunde inte starta betalningen'); setBusy(false); }
  };
  return (
    <div data-testid="charge-box" style={{ padding: 12, borderRadius: RADIUS.md, border: `1px solid ${charge.status === 'unpaid' ? T.accentFill : T.border}`, display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: 17 }}>{kr(charge.totalKr)}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      <SummaryLines summary={{ lines: charge.lines, totalKr: charge.totalKr, preliminary: charge.preliminary, notes: [] }} compact />
      {charge.status === 'unpaid' && <>
        {charge.dueDate && <div style={{ fontSize: 13, color: charge.overdue ? T.danger : T.textSub }}>Förfaller {charge.dueDate}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button onClick={pay} disabled={busy}>Betala med kort</Button>
          <span style={{ fontSize: 13, color: T.textSub }}>eller Swish/kontant till jaktledaren, som markerar betalt.</span>
        </div>
      </>}
      {charge.status === 'paid' && charge.paidAt && <div style={{ fontSize: 13, color: T.textSub }}>Betald {charge.paidAt.slice(0, 10)}</div>}
      {err && <div role="alert" style={{ color: T.danger, fontSize: 14, fontWeight: 600 }}>{err}</div>}
    </div>
  );
}

function MyReport({ markId, report, onChanged }: { markId: string; report: KillReport; onChanged: (r: KillReport) => void }) {
  const T = useTheme();
  const [editing, setEditing] = useState(report.status === 'pending');
  const st = reportStatusText(report);
  return (
    <div data-testid="my-report" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700 }}>Ditt fällda vilt</span><Badge tone={st.tone}>{st.label}</Badge>
      </div>
      {editing
        ? <KillReportForm markId={markId} report={report} mode="hunter" onDone={r => { setEditing(false); onChanged(r); }} onCancel={report.status === 'pending' ? undefined : () => setEditing(false)} />
        : <>
          {report.items.length > 0 && !(report.charge && report.charge.status !== 'void') && <SummaryLines summary={report.summary} compact />}
          {report.status === 'submitted' && <div><Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Ändra</Button></div>}
          {report.status === 'confirmed' && !report.charge && <div style={{ fontSize: 14, color: T.textSub }}>Inget att betala.</div>}
        </>}
      {report.charge && report.charge.status !== 'void' && <ChargeBox charge={report.charge} />}
    </div>
  );
}

function LeaderRow({ markId, report, duplicate, onChanged, onRemoved }: {
  markId: string; report: KillReport; duplicate: boolean; onChanged: (r: KillReport) => void; onRemoved: (reportId: string) => void;
}) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const st = reportStatusText(report);
  const run = async (fn: () => Promise<KillReport | boolean>, done?: string) => {
    setBusy(true); setMsg('');
    try {
      const r = await fn();
      if (typeof r === 'object') { onChanged(r); notifyHuntEconomyChanged(); }
      if (done) setMsg(done);
    } catch (e: any) { setMsg(e.message || 'Något gick fel'); }
    setBusy(false);
  };
  // EJ1/EJ2: ta bort rapporten eller avgiften (servern kontrollerar att man är jaktledare/markägare/admin).
  const remove = async () => {
    if (!confirm(deleteReportConfirmText(report))) return;
    setBusy(true); setMsg('');
    try { await huntApi.deleteKillReport(report.id); onRemoved(report.id); notifyHuntEconomyChanged(); }
    catch (e: any) { setMsg(e.message || 'Något gick fel'); setBusy(false); }
  };
  const removeCharge = async () => {
    const c = report.charge;
    if (!c || !confirm(deleteChargeConfirmText(c))) return;
    setBusy(true); setMsg('');
    try { await huntApi.deleteHuntCharge(c.id); onChanged({ ...report, charge: null }); notifyHuntEconomyChanged(); }
    catch (e: any) { setMsg(e.message || 'Något gick fel'); }
    setBusy(false);
  };
  return (
    <div data-testid="leader-report" style={{ borderTop: `1px solid ${T.border}`, padding: '12px 0', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, flex: '1 1 140px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{report.hunterName || report.hunterEmail}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        {report.charge && report.charge.status !== 'void' && <Badge tone={chargeStatusText(report.charge).tone}>{kr(report.charge.totalKr)} · {chargeStatusText(report.charge).label.toLowerCase()}</Badge>}
        {!open && <IconButton label={`Ta bort ${report.hunterName || report.hunterEmail}s rapport`} disabled={busy} onClick={() => void remove()}><Icon as={Trash2} /></IconButton>}
      </div>
      {report.items.length > 0 && !open && (
        <div style={{ fontSize: 14, color: T.textSub }}>
          {report.items.map(i => `${i.antal > 1 ? `${i.antal} × ` : ''}${i.viltslag}${i.kon && i.kon !== 'Okänt' ? ` ${i.kon.toLowerCase()}` : ''}${i.notAllowed ? ' (ej tillåtet)' : ''}`).join(', ')}
          {report.status !== 'confirmed' && <> · {kr(report.summary.totalKr)}</>}
        </div>
      )}
      {duplicate && report.status !== 'confirmed' && <div style={{ fontSize: 13, color: T.danger, fontWeight: 600 }}>Möjlig dubblett – samma viltslag har rapporterats av flera jägare.</div>}
      {open
        ? <KillReportForm markId={markId} report={report} mode="leader" onDone={r => { setOpen(false); onChanged(r); }} onCancel={() => setOpen(false)} />
        : <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {report.status === 'submitted' && <Button size="sm" disabled={busy} onClick={() => { if (confirm(`Bekräfta ${report.hunterName || report.hunterEmail}s rapport${report.summary.totalKr ? ` (${kr(report.summary.totalKr)})` : ''}?`)) void run(() => huntApi.confirmKillReport(report.id)); }}><Icon as={Check} size={16} /> Bekräfta</Button>}
          {report.status !== 'confirmed' && <Button size="sm" variant="secondary" disabled={busy} onClick={() => setOpen(true)}>{report.status === 'pending' ? 'Fyll i åt jägaren' : 'Rätta'}</Button>}
          {report.status === 'pending' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => huntApi.remindKillReport(report.id), 'Påminnelse skickad')}>Påminn{report.reminderCount ? ` (${report.reminderCount})` : ''}</Button>}
          {report.status === 'confirmed' && report.charge?.status !== 'paid' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => { if (confirm('Öppna rapporten igen? En obetald avgift stryks och räknas om när du bekräftar på nytt.')) void run(() => huntApi.reopenKillReport(report.id)); }}>Öppna igen</Button>}
          {report.charge && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void removeCharge()}><Icon as={Trash2} size={16} /> Ta bort avgiften</Button>}
        </div>}
      {msg && <div role="status" style={{ fontSize: 13, color: T.textSub }}>{msg}</div>}
    </div>
  );
}

function HuntCard({ markId, hunt, userEmail, focused, onReport, onRemoved }: {
  markId: string; hunt: AfterHunt; userEmail: string; focused?: boolean; onReport: (r: KillReport) => void; onRemoved: (reportId: string) => void;
}) {
  const T = useTheme();
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState('');
  const join = async () => {
    setJoining(true); setJoinErr('');
    try { onReport(await huntApi.joinKillReport(hunt.meetingId)); notifyHuntEconomyChanged(); } catch (e: any) { setJoinErr(e.message || 'Något gick fel'); }
    setJoining(false);
  };
  const mine = hunt.reports.find(r => r.mine || (!!userEmail && r.hunterEmail === userEmail.toLowerCase()));
  const others = hunt.canConfirm ? hunt.reports : [];
  const dups = useMemo(() => possibleDuplicates(hunt.reports), [hunt.reports]);
  return (
    <Card data-testid="after-hunt" data-meeting={hunt.meetingId} style={{ display: 'grid', gap: 14, ...(focused ? { borderColor: T.accentFill, boxShadow: `0 0 0 2px ${T.accentSoft}` } : {}) }}>
      <div>
        <div style={{ ...TYPE.title }}>{hunt.title || 'Jakt'}</div>
        <div style={{ ...TYPE.small, color: T.textSub }}>
          {dateText(hunt.startedAt)} {timeText(hunt.startedAt)}{hunt.endedAt ? `–${timeText(hunt.endedAt)}` : ''}{hunt.open ? ' · pågår' : ''}
          {hunt.canConfirm && ` · ${hunt.counts.confirmed} bekräftade, ${hunt.counts.submitted} väntar, ${hunt.counts.pending} har inte svarat`}
        </div>
      </div>
      {mine && <MyReport markId={markId} report={mine} onChanged={onReport} />}
      {!mine && hunt.canJoin && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...TYPE.small, color: T.textSub }}>Var du med i jakten?</span>
          <Button size="sm" variant="secondary" disabled={joining} onClick={join}>Jag var med – ange fällt vilt</Button>
          {joinErr && <span role="alert" style={{ color: T.danger, fontSize: 14 }}>{joinErr}</span>}
        </div>
      )}
      {others.length > 0 && (
        <div>
          <div style={{ ...TYPE.label, color: T.textSub }}>Jägarna (du bekräftar)</div>
          {others.map(r => <LeaderRow key={r.id} markId={markId} report={r} duplicate={r.items.some(i => dups.has(i.id))} onChanged={onReport} onRemoved={onRemoved} />)}
        </div>
      )}
    </Card>
  );
}

export function EfterJaktTab({ markId, userEmail }: { markId: string; userEmail?: string | null }) {
  const T = useTheme();
  const [hunts, setHunts] = useState<AfterHunt[] | null>(null);
  // Tryck på en pushnotis: den jakten först och markerad (kallstart: App.tsx sparar den innan marken laddats).
  const [focus] = useState<string | null>(() => { try { const f = sessionStorage.getItem(EFTERJAKT_FOCUS_KEY); sessionStorage.removeItem(EFTERJAKT_FOCUS_KEY); return f; } catch { return null; } });
  useEffect(() => {
    if (!focus || !hunts) return;
    document.querySelector(`[data-meeting="${CSS.escape(focus)}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focus, hunts]);
  const [err, setErr] = useState('');
  const load = () => huntApi.markAfterHunts(markId).then(h => { setHunts(h); setErr(''); }).catch(e => { setErr(e.message); setHunts([]); });
  useEffect(() => { void load(); }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = () => void load();
    window.addEventListener(HUNT_ECONOMY_CHANGED, on);
    return () => window.removeEventListener(HUNT_ECONOMY_CHANGED, on);
  }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onReport = (r: KillReport) => setHunts(hs => hs && hs.map(h => (h.meetingId === r.meetingId
    ? { ...h, canJoin: false, reports: h.reports.some(x => x.id === r.id) ? h.reports.map(x => (x.id === r.id ? r : x)) : [r, ...h.reports] } : h)));
  const onRemoved = (reportId: string) => setHunts(hs => hs && hs.map(h => ({ ...h, reports: h.reports.filter(x => x.id !== reportId) })));
  // EJ3: listan fälls in och ut; läget sparas per användare (en pushnotis öppnar den alltid).
  const collapseKey = efterJaktCollapsedKey(userEmail);
  const [collapsed, setCollapsed] = useState(() => !focus && readCollapsed(collapseKey));
  const toggle = () => setCollapsed(c => { writeCollapsed(collapseKey, !c); return !c; });

  if (!hunts) return <Loader text="Laddar jakterna…" />;
  return (
    <div style={{ padding: '0 clamp(10px,3vw,28px) 32px', display: 'grid', gap: 16, maxWidth: 820 }}>
      <div>
        <h2 style={{ ...TYPE.section, margin: '8px 0 4px' }}>Efter jakt</h2>
        <div style={{ ...TYPE.small, color: T.textSub }}>Ange vad du fällde i varje jakt – direkt eller senare. Jaktledaren bekräftar, sedan räknas fällavgifter och eventuella böter.</div>
      </div>
      {err && <div role="alert" style={{ color: T.danger }}>{err}</div>}
      {hunts.length === 0 && <EmptyState title="Inget att rapportera" sub="När en jakt på marken är slut anger du ditt fällda vilt här – även i efterhand. Vilt som du registrerar under jakten (på kartan eller via Q) kommer med automatiskt." />}
      {hunts.length > 0 && (
        <button type="button" data-testid="efterjakt-toggle" aria-expanded={!collapsed} aria-controls="efterjakt-list" onClick={toggle}
          style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: 0, border: 'none', background: 'none', color: T.text, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ display: 'inline-flex', transition: 'transform .15s', transform: collapsed ? 'rotate(-90deg)' : 'none' }}><Icon as={ChevronDown} size={20} /></span>
          <span style={{ ...TYPE.title }}>Rapporter efter jakt</span>
          <span style={{ ...TYPE.small, color: T.textSub }}>{hunts.length} {hunts.length === 1 ? 'jakt' : 'jakter'}</span>
        </button>
      )}
      {!collapsed && <div id="efterjakt-list" style={{ display: 'grid', gap: 16 }}>
        {hunts.map(h => <HuntCard key={h.meetingId} markId={markId} hunt={h} userEmail={userEmail ?? ''} focused={h.meetingId === focus} onReport={onReport} onRemoved={onRemoved} />)}
      </div>}
    </div>
  );
}
